/*
Copyright (c) 2015, salesforce.com, inc. All rights reserved.
Copyright (c) 2017, Picsa Advisory Services (Pty) Ltd. All rights reserved.

Redistribution and use in source and binary forms, with or without modification, are permitted provided that the following conditions are met:
Redistributions of source code must retain the above copyright notice, this list of conditions and the following disclaimer.
Redistributions in binary form must reproduce the above copyright notice, this list of conditions and the following disclaimer in the documentation and/or other materials provided with the distribution.
Neither the name of salesforce.com, inc. nor the names of its contributors may be used to endorse or promote products derived from this software without specific prior written permission.

THIS SOFTWARE IS PROVIDED BY THE COPYRIGHT HOLDERS AND CONTRIBUTORS "AS IS" AND ANY EXPRESS OR IMPLIED WARRANTIES, INCLUDING, BUT NOT LIMITED TO, THE IMPLIED WARRANTIES OF MERCHANTABILITY AND FITNESS FOR A PARTICULAR PURPOSE ARE DISCLAIMED. IN NO EVENT SHALL THE COPYRIGHT OWNER OR CONTRIBUTORS BE LIABLE FOR ANY DIRECT, INDIRECT, INCIDENTAL, SPECIAL, EXEMPLARY, OR CONSEQUENTIAL DAMAGES (INCLUDING, BUT NOT LIMITED TO, PROCUREMENT OF SUBSTITUTE GOODS OR SERVICES LOSS OF USE, DATA, OR PROFITS OR BUSINESS INTERRUPTION) HOWEVER CAUSED AND ON ANY THEORY OF LIABILITY, WHETHER IN CONTRACT, STRICT LIABILITY, OR TORT (INCLUDING NEGLIGENCE OR OTHERWISE) ARISING IN ANY WAY OUT OF THE USE OF THIS SOFTWARE, EVEN IF ADVISED OF THE POSSIBILITY OF SUCH DAMAGE.
*/

import React from 'react'
import PropTypes from 'prop-types'
import { graphql, compose } from 'react-apollo'
import gql from 'graphql-tag'
import { default as fragments } from '../../fragments'
import moment from 'moment'
import DatePicker from 'react-datepicker'

import {
  SvgIcon,
  Button,
  TextArea,
  CaseChoosePriority,
  DockedFormFooter,
  Modal,
  CaseCreateAssign,
  SpanWithData,
  Select,
  SelectWithData
} from '../'

import {
  CaseEditToolbarItem as ToolbarItem
} from './CaseEditToolbarItem'

import {
  getAvatarInitialsColour
} from '../../utils'

import { CASE_TYPE_MAP_EDIT } from '../../constants'


export class CaseEdit extends React.PureComponent {
  static propTypes = {
    caseId: PropTypes.string.isRequired,
    caseData: PropTypes.object.isRequired,
    form: PropTypes.oneOf([ "header", "reassign", "entry" ]),
    onSave: PropTypes.func,
    onCancel: PropTypes.func,
    onFail: PropTypes.func,
    onReassignClick: PropTypes.func
  }

  static defaultProps = {
    form: "header"
  }

  constructor(props) {
    super(props)

    this.state = {
      productType: props.caseData.productType.toLowerCase() || '',
      requestType: props.caseData.requestType,
      assignedTo: props.caseData.assignedTo.id,
      nextActionAt: props.nextActionAt,
      priority: props.caseData.priority,
      status: props.caseData.status,
      caseNote: '',
      selectProductRequestType: false,
      selectAssignedTo: props.reassign,
      selectNextActionAt: false,
      selectPriority: false,
      showResolveModal: false
    }
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    if (nextProps.nextActionAt !== this.state.nextActionAt &&
      nextProps.nextActionAt !== this.props.nextActionAt) {
      this.setState({ nextActionAt: nextProps.nextActionAt })
    }

    if (nextProps.reassign !== undefined) {
      this.setState({ selectAssignedTo: nextProps.reassign })
    }
  }

  saveCase = (fieldsToSave) => {
    let input = {
      id: this.props.caseId
    }

    fieldsToSave.forEach(field => {
      if (field === 'nextActionAt' &&
        !moment(this.state[field]).isSame(moment(this.props.caseData[field]))) {
        input[field] = this.state[field]
          .utc()
          .format("YYYY-MM-DD HH:mm:ss.SSS");
      } else if (this.state[field] !== this.props.caseData[field] && field !== 'caseNote') {
        input[field] = this.state[field]
      }
    })

    if (Object.keys(input).length > 1) {
      this.props.saveCase({ variables: { input }})
      .then(({ data }) => {
        console.log(`Case ${fieldsToSave} saved successfully`)
        if (fieldsToSave.includes("caseNote") && this.state.caseNote.length > 0) {
          this.saveCaseNote(data.caseMutate.case.id)
        } else if (this.props.onSave) {
          this.props.onSave()
        }
      })
      .catch((error) => {
        console.log(`Case ${fieldsToSave} save failed!`, error)
        if (this.props.onFail) {
          this.props.onFail()
        }
      })
    }
  }

  saveCaseNote = (caseId) => {
    let input = {
      case: caseId,
      note: this.state.caseNote,
      author: localStorage.getItem("userUUID"),
      direction: "internal"
    }
     this.props.saveCaseNote({ variables: { input } })
    .then(() => {
      console.log("Case note saved successfully")
      this.setState({ caseNote: '' })
      if (this.props.onSave) {
        this.props.onSave()
      }
    })
    .catch((error) => {
      console.log('Case note save failed!', error)
      this.setState({ caseNote: '' })
      if (this.props.onFail) {
        this.props.onFail()
      }
    })
  }

  handleCaseStatusChange = (status, caseNote) => {
    this.setState({ status }, () =>
      caseNote
        ? this.saveCase(["status", "caseNote"])
        : this.saveCase(["status"])
    )
  }

  getPriorityIcon = (priority) => (
    {
      'A': "priority_high",
      'B': "priority_medium",
      'C': "priority_low"
    }[priority]
  )

  render() {
    let caseInfo = this.props.caseData
    let assignedToName = `${caseInfo.assignedTo.firstName} ${caseInfo.assignedTo.lastName}`
    let assignedToInitials = `${caseInfo.assignedTo.firstName[0]}${caseInfo.assignedTo.lastName[0]}`
    let avatarInitialsColour = getAvatarInitialsColour(assignedToInitials)
    let caseResolved = caseInfo.status === 'RESOLVED'
    let requestTypeChoices = CASE_TYPE_MAP_EDIT[this.state.productType]

    let { caseId, form, onClickOnOtherToolbarItem, onReassignClick, onCancel, onSave, onFail } = this.props

    if (form === "header") {
      return (
        <div className='slds-size_1-of-1 action-toolbar slds-wrap'>
          {this.state.showResolveModal &&
            <Modal
              heading={`Resolve this case for ${caseInfo.affects.firstName}`}
              subHeading="Add a short descriptive closing case note"
              body={
                <TextArea
                  rows={5}
                  onChange={(e) => this.setState({ caseNote: e.target.value })}
                />
              }
              footer={
                <div className="slds-align_absolute-center">
                  <Button
                    onClick={() => (
                      this.setState({
                        showResolveModal: false,
                        caseNote: ''
                      })
                    )}>
                    Cancel
                  </Button>
                  <Button
                    type="brand"
                    onClick={() => {
                      this.handleCaseStatusChange("resolved", true)
                      this.setState({
                        showResolveModal: false
                      })
                    }}>
                    Resolve
                  </Button>
                </div>
              }
              selected={this.state.selectAssignTo}
            />
          }

          <ToolbarItem
            title='Product and request type'
            value={
              <div className={`case-assignee slds-dropdown-trigger slds-dropdown-trigger_click${this.state.selectProductRequestType ? " slds-is-open": ''}`}>
                <div>
                  <SpanWithData
                    forField="CaseProductType"
                    searchKey={caseInfo.productType}
                  />
                  {`, ${caseInfo.requestType}`}
                </div>
                <div className="slds-dropdown slds-dropdown_left slds-p-around_medium" style={{ width: 300 }}>
                  <div className='slds-m-bottom_xx-large'>
                    <SelectWithData
                      id="productType"
                      label="Product"
                      forField="CaseProductType"
                      value={this.state.productType}
                      onChange={e => this.setState({ productType: e.target.value, requestType: ''})}
                    />
                    <Select
                      id="requestType"
                      options={requestTypeChoices}
                      value={this.state.requestType}
                      onChange={e => this.setState({ requestType: e.target.value })}
                      disabled={this.state.productType === ''}
                    />
                  </div>
                  <DockedFormFooter
                    onCancel={() => this.setState({ selectProductRequestType: false })}
                    onSave={() => {
                      this.saveCase(['productType', 'requestType'])
                      this.setState({ selectProductRequestType: false })
                    }}
                    style={{ zIndex: 0 }}
                  />
                </div>
              </div>
            }
            trigger={
              <Button
                type={this.state.selectProductRequestType ? "inverse" : "reset"}
                onClick={() => {
                  if (caseResolved) {
                    return false
                  } else {
                    this.setState({
                      selectProductRequestType: true,
                      selectAssignedTo: false,
                      selectNextActionAt: false,
                      selectPriority: false,
                    }, onClickOnOtherToolbarItem)
                  }
                }}
                disabled={caseResolved}
                className={this.state.selectProductRequestType ? '' : "slds-p-right_medium"}>
                Edit
              </Button>
            }
          />

          <ToolbarItem
            title='Assigned to'
            value={
              <div className="case-assignee slds-truncate">
                <div className="slds-avatar slds-avatar_small slds-avatar_circle slds-m-right_x-small">
                  <abbr
                    className={`slds-avatar__initials slds-icon-standard-user ${avatarInitialsColour ? `avatar-color_${avatarInitialsColour}` : ''}`}
                    title={assignedToName}>
                    {assignedToInitials}
                  </abbr>
                </div>
                <div className="text-follows-icon case-assignee_name">
                  {assignedToName}
                </div>
              </div>
            }
            trigger={
              <Button
                type={this.state.selectAssignedTo ? "inverse" : "reset"}
                onClick={() => {
                  this.setState((prevState) => ({
                    selectAssignedTo: !prevState.selectAssignedTo,
                    selectPriority: false,
                    selectNextActionAt: false
                  }))
                  onReassignClick()
                }}
                disabled={caseResolved}
                className={this.state.selectAssignTo ? '' : "slds-p-right_medium"}>
                Reassign
              </Button>
            }
          />

          <ToolbarItem
            title='Next action'
            value={
              <div className={`case-assignee slds-dropdown-trigger slds-dropdown-trigger_click${this.state.selectNextActionAt ? " slds-is-open" : ''}`}>
                <SvgIcon
                  className="slds-m-right_x-small"
                  variant="picsaos"
                  symbol="calendar"
                  size="small"
                />
                <div className="text-follows-icon">
                  {this.state.nextActionAt
                    ? moment(this.state.nextActionAt).format("ddd D MMM [at] HH:mm")
                    : '-'
                  }
                </div>
                <div className="slds-dropdown slds-dropdown_left slds-text-align_left">
                  <div
                    className="datepicker-no_padding_right"
                    style={{ minWidth: 400, minHeight: 300 }}>
                    <DatePicker
                      selected={this.state.nextActionAt
                        ? moment(this.state.nextActionAt)
                        : undefined
                      }
                      onChange={(date) => this.setState({ nextActionAt: date })}
                      minDate={moment()}
                      openToDate={moment()}
                      showTimeSelect
                      timeFormat="HH:mm"
                      minTime={moment().hours(8).minutes(0)}  // working hours
                      maxTime={moment().hours(17).minutes(0)}
                      inline={true}
                      disabled={caseResolved}>
                      <DockedFormFooter
                        onCancel={() => this.setState({
                          selectNextActionAt: false,
                          nextActionAt: caseInfo.nextActionAt
                        })}
                        onSave={() => {
                          this.saveCase(["nextActionAt"])
                          this.setState({ selectNextActionAt: false })
                        }}
                      />
                    </DatePicker>
                  </div>
                </div>
              </div>
            }
            trigger={
              <SvgIcon
                className="slds-m-right_x-small"
                variant="utility"
                colour={caseResolved ? 'light' : 'default'}
                symbol="chevrondown"
                size="xx-small"
              />
            }
            onClick={() => {
              if (caseResolved) {
                return false
              } else {
                this.setState({
                  selectNextActionAt: true,
                  selectPriority: false,
                  selectAssignedTo: false
                }, onClickOnOtherToolbarItem)
              }
            }}
          />

          <ToolbarItem
            title='Priority'
            value={
              <div className={`case-assignee slds-dropdown-trigger slds-dropdown-trigger_click${this.state.selectPriority ? " slds-is-open": ''}`}>
                <SvgIcon
                  className="slds-m-right_x-small"
                  variant="picsaos"
                  symbol={this.getPriorityIcon(caseInfo.priority)}
                  size="small"
                />
                <div className="text-follows-icon">
                  <SpanWithData
                    forField="CasePriority"
                    searchKey={caseInfo.priority}
                  />
                </div>
                <div className="slds-dropdown slds-dropdown_left slds-p-around_medium">
                  <CaseChoosePriority
                    case={caseId}
                    priority={caseInfo.priority}
                    onClose={() => this.setState({ selectPriority: false })}
                  />
                </div>
              </div>
            }
            trigger={
              <SvgIcon
                className="slds-m-right_x-small"
                variant="utility"
                symbol="chevrondown"
                colour={caseResolved ? 'light' : 'default'}
                size="xx-small"
              />
            }
            onClick={() => {
              if (caseResolved) {
                return false
              } else {
                this.setState({
                  selectPriority: true,
                  selectNextActionAt: false,
                  selectAssignedTo: false
                }, onClickOnOtherToolbarItem)
              }
            }}
          />

          {caseInfo.status === "OPEN"
            ? <ToolbarItem
                title='Status'
                value={
                  <div className="case-assignee">
                    <div className="text-follows-icon">
                      <SpanWithData
                        forField="CaseStatus"
                        searchKey={caseInfo.status}
                      />
                    </div>
                  </div>
                }
                trigger={
                  <Button
                    type="brand"
                    onClick={() => this.setState({ showResolveModal: true }, onClickOnOtherToolbarItem)}>
                    RESOLVE
                  </Button>
                }
              />
            : <ToolbarItem
                  title='Status'
                  value={
                    <div className="slds-col">
                      <div className="case-assignee">
                        <div className="text-follows-icon">Resolved</div>
                      </div>
                    </div>
                  }
                  trigger={
                    <Button
                      className="slds-button_inverse"
                      onClick={() => this.handleCaseStatusChange("open")}>
                      RE-OPEN
                    </Button>
                  }
                />
          }
        </div>
      )
    } else if (form === "reassign") {
      return (
        <CaseCreateAssign
          case={caseId}
          affects={caseInfo.affects.id}
          productType={caseInfo.productType}
          requestType={caseInfo.requestType}
          assignedTo={caseInfo.assignedTo.id}
          nextActionAt={caseInfo.nextActionAt}
          priority={caseInfo.priority}
          onSave={this.props.onSave}
          onCancelClick={() => {
            this.setState({ selectAssignedTo: false })
            if (onCancel) {
              onCancel()
            }
          }}
          onCaseNoteSaveSuccess={onSave}
          onCaseSaveFail={onFail}
          onCaseNoteSaveFail={onFail}
          assign={true}
        />
      )
    }
  }
}

const saveCase = gql`
  mutation($input: CaseMutationInput!) {
    caseMutate(input: $input) {
      case {
        id
        priority
        nextActionAt
        status
        productType
        requestType
        assignedTo {
          id
          firstName
          lastName
        }
      }
    }
  }
`
 const saveCaseNote = gql`
  mutation saveCaseNote($input: CaseNoteMutationInput!) {
    casenoteMutate(input: $input) {
      casenote {
        ...CaseNote
      }
    }
  }
  ${fragments.casenote}
`
 const withCaseMutate =
  graphql(saveCase, {
    name: "saveCase"
  })
 const withCaseNoteMutate =
  graphql(saveCaseNote, {
    name: "saveCaseNote"
  })
 export default compose(
  withCaseMutate,
  withCaseNoteMutate
)(CaseEdit)



// WEBPACK FOOTER //
// ./src/components/case-edit/index.js